import { orderBy } from 'lodash';
import type {
  GroupedFormOption,
  BaseFormOption,
} from 'src/components/form/form-option';
import { cn } from 'src/utils/cn';

export type SortByOptions = 'label' | 'value' | 'manual';

export function applySortBy<T extends BaseFormOption<string | null>>(
  sortBy: SortByOptions,
  options: T[]
) {
  switch (sortBy) {
    case 'manual':
      return options;
    case 'label':
      return orderBy(
        options,
        // place empty value on top, no matter what the actual label is
        [(item) => (item.value ? item.label.toLowerCase() : '')],
        ['asc']
      );
    case 'value':
      return orderBy(
        options,
        // place empty value on top, no matter what the actual label is
        [(item) => (item.value ? item.value.toLowerCase() : '')],
        ['asc']
      );
  }
}

export function applyGroupSortBy<T extends GroupedFormOption>(
  sortBy: SortByOptions,
  options: T[]
) {
  switch (sortBy) {
    case 'manual':
      return options;
    case 'label':
    case 'value': // groups have no value, so we treat it as label
      return orderBy(options, [(item) => item.label.toLowerCase()], ['asc']);
  }
}

export const selectOptionBaseClasses = cn(
  `
    text-foreground-default select-none
    not-legacy:rounded-xs not-legacy:px-lg not-legacy:py-sm
    not-legacy:text-label-base
    aria-[disabled]:cursor-not-allowed
    aria-[disabled]:bg-background-surface-disabled
    aria-[disabled]:text-foreground-surface-disabled
  `,

  `
    not-legacy:[[data-active-item][aria-disabled]]:outline-2
    not-legacy:[[data-active-item][aria-disabled]]:-outline-offset-2
    not-legacy:[[data-active-item][aria-disabled]]:outline-background-primary-disabled
    not-legacy:[[data-active-item][aria-disabled]]:hover:outline-2
    not-legacy:[[data-active-item][aria-disabled]]:hover:-outline-offset-2
    not-legacy:[[data-active-item][aria-disabled]]:hover:outline-background-primary-disabled
  `,
  `
    legacy:m-[0.1rem] legacy:rounded-xs legacy:p-[1rem] legacy:pl-[2rem]
    legacy:text-legacy-brand
    legacy:aria-[disabled]:border-legacy-inactive
    legacy:aria-[disabled]:bg-legacy-inactive-lighter
    legacy:aria-[disabled]:text-legacy-inactive
    legacy:[[data-focus-visible='true']_&[data-active-item]]:legacy-focus-style
  `,
  // see https://github.com/ariakit/ariakit/issues/845#issuecomment-785493623
  '[pointer-events:initial]!'
);
